import { useEffect, useRef, useCallback, useImperativeHandle, forwardRef, useState, useMemo, type MutableRefObject } from 'react';
import * as monaco from 'monaco-editor';
import { isScriptPath } from './projectPaths';
import { migrateV1Project, treePathOfV1Path, type ProjectTree } from '@gotti/sandbox-core';
import type { SandboxSystems, SystemsChangedCallback } from './types';
import { configureMonaco, monacoModelFactory, validateFile, refreshAssetPathTypes } from './monacoUtils';
import { useProjectEditor, type EditorModel, type ProjectChangedCallback } from './useProjectEditor';
import { FileTree } from './FileTree';
import { EditorTabs } from './EditorTabs';
import { MonacoEditor, type RevealTarget } from './MonacoEditor';
import { TestRunnerPanel } from './TestRunnerPanel';
import { DesignDocsPanel } from '../design';
import { KnowledgeGraphPanel } from '../knowledge';
import { AssetsPanel } from '../assets';
import { subscribe as subscribeAssets, listAssets } from '../assets/assetStore';
import { syncKnowledgeGraphFiles } from '../knowledge/syncFiles';

export interface SandboxEditorHandle {
  getAllSystems: () => SandboxSystems;
  resetToDefaults: () => void;
  setDefaults: (systems: SandboxSystems) => void;
  loadSystems: (systems: SandboxSystems) => void;
  setOnSystemsChanged: (callback: SystemsChangedCallback) => void;
  switchToFolder: (folder: 'client' | 'server' | 'shared', subfolder?: string) => void;
  /** Get Monaco models map, keyed by tree path (for testing) */
  getModels: () => Map<string, monaco.editor.ITextModel>;
  /** Add a new file to the specified folder (for testing); returns its tree path */
  addNewFile: (folder: 'client' | 'server' | 'shared', subfolder: string | null, name: string) => string | null;
  /** Add a new subfolder to a top-level folder (for testing) */
  addFolder: (folder: 'client' | 'server' | 'shared', subfolderName: string) => boolean;
  /** Update a file's content by path (for AI agent integration) */
  updateFile: (path: string, content: string) => boolean;
  /**
   * Validate ALL files through Monaco's TypeScript worker.
   * Returns per-file diagnostics filtered through shouldSuppressDiagnostic
   * (same filter used by the backend TypeCheckTool).
   * Used by e2e tests to verify frontend/backend TS error parity.
   */
  validateAllFiles: () => Promise<{ path: string; errors: string[] }[]>;
  markCodeCheckpoint: () => void;
  /** The project tree as it is now — what a save, fork or export sends. */
  getProject: () => ProjectTree;
  /** Opens a project tree — a loaded or imported document's. */
  loadProject: (tree: ProjectTree) => void;
  /** Every file the AI sees, at its v1 path (`client/…`, `__tests__/…`), in tree order — generated files left out. */
  getAiFiles: () => { path: string; content: string }[];
}

interface SandboxEditorReactProps {
  initialSystems: SandboxSystems;
  /** Hears every change made to the project — never a load: what the save state counts as unsaved. */
  onProjectChanged?: ProjectChangedCallback;
}

const SIDEBAR_MIN_WIDTH = 150;
const SIDEBAR_MAX_WIDTH = 600;

/**
 * Drag-to-resize the `.file-explorer` sidebar. Mirrors the editor↔preview
 * resizer in main.tsx — keeps state on the DOM (style.width) so the existing
 * panel-resizer keeps reading the live `sidebar.offsetWidth`.
 */
function useSidebarResizer() {
  const resizerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resizer = resizerRef.current;
    if (!resizer) return;
    const sidebar = document.querySelector('.file-explorer') as HTMLElement | null;
    if (!sidebar) return;

    let isDragging = false;
    // Grab-relative drag state (mirrors the editor↔preview resizer in main.tsx):
    // resize by the cursor's delta so the sidebar edge tracks the pointer exactly.
    let startX = 0;
    let startWidth = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      startX = e.clientX;
      startWidth = sidebar.getBoundingClientRect().width;
      resizer.classList.add('active');
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const newWidth = startWidth + (e.clientX - startX);
      const width = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, newWidth));
      sidebar.style.width = `${width}px`;
    };

    const onMouseUp = () => {
      if (!isDragging) return;
      isDragging = false;
      resizer.classList.remove('active');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.dispatchEvent(new Event('resize'));
    };

    resizer.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      resizer.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  return resizerRef;
}

export const SandboxEditorReact = forwardRef<SandboxEditorHandle, SandboxEditorReactProps>(
  function SandboxEditorReact({ initialSystems, onProjectChanged }, ref) {
    const models = useRef<Map<string, monaco.editor.ITextModel>>(new Map());
    const onSystemsChangedRef = useRef<SystemsChangedCallback | null>(null);
    const defaultSystemsRef = useRef<SandboxSystems>(initialSystems);
    const [modelsReady, setModelsReady] = useState(false);
    const [sidebarTab, setSidebarTab] = useState<'files' | 'tests' | 'design' | 'graph' | 'assets'>('files');
    // Pending request to scroll the Monaco editor to a line (e.g. after the
    // Tests panel asks to open a spec at a clicked test/assertion). The nonce
    // makes repeated clicks on the same line re-trigger the jump.
    const [revealTarget, setRevealTarget] = useState<RevealTarget | null>(null);
    const revealNonce = useRef(0);
    const sidebarResizerRef = useSidebarResizer();
    const handleSystemsChanged: SystemsChangedCallback = useCallback((type, folder) => {
      onSystemsChangedRef.current?.(type, folder);
    }, []);

    const initialProject = useMemo(() => migrateV1Project(initialSystems, { name: 'sandbox' }), [initialSystems]);
    const editor = useProjectEditor(
      initialProject,
      models as unknown as MutableRefObject<Map<string, EditorModel>>,
      monacoModelFactory,
      handleSystemsChanged,
      onProjectChanged,
    );
    const { tree, errors, currentPath, openTabs, expandedFolders, syncEpoch, getAllSystems, undo, redo } = editor;

    // Initialize Monaco and create models
    useEffect(() => {
      configureMonaco();
      editor.mountModels();
      setModelsReady(true);
      console.log('[SandboxEditorReact] Models created, count:', models.current.size);

      // Validate all files after a short delay
      const timeout = setTimeout(async () => {
        for (const path of Object.keys(tree.files).filter(isScriptPath)) {
          editor.setFileErrors(path, await validateFile(models.current, path));
        }
      }, 100);

      return () => {
        clearTimeout(timeout);
        setModelsReady(false);
        editor.releaseModels();
      };
      // Mount and unmount only: the hook keeps the models in step with the tree from here on.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Undo/redo keyboard handler
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        const isEditorFocused = document.activeElement?.closest('.monaco-editor');
        if (isEditorFocused) return;

        if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
          if (e.shiftKey) {
            e.preventDefault();
            redo();
          } else {
            e.preventDefault();
            undo();
          }
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
          e.preventDefault();
          redo();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [undo, redo]);

    // Keep Monaco asset-path autocomplete in sync with the virtual /assets store
    useEffect(() => {
      refreshAssetPathTypes(listAssets().map((a) => a.path));
      return subscribeAssets(() => refreshAssetPathTypes(listAssets().map((a) => a.path)));
    }, []);

    // Lazy KG file sync: reconcile current files into the graph when the Graph
    // tab is open and a coarse checkpoint fires (tab-open or syncEpoch bump from
    // structural edits / Run / Save). Reads live Monaco models, so all content
    // edits made before the checkpoint are reflected. Never runs per keystroke.
    useEffect(() => {
      if (sidebarTab !== 'graph') return;
      try {
        syncKnowledgeGraphFiles(getAllSystems());
      } catch (err) {
        console.error('[KG] file sync failed', err);
      }
      // getAllSystems intentionally omitted from deps — sync on tab-open + epoch only.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sidebarTab, syncEpoch]);

    /** The first file under `folder` to open: its `systems/` folder's first, then its own files, then anything under it. */
    const firstFileUnder = useCallback((folder: string): string | null => {
      const paths = Object.keys(editor.getProject().files).filter((path) => path.startsWith(`${folder}/`) && !/\.(spec|test)\.tsx?$/.test(path));
      return paths.find((path) => path.startsWith(`${folder}/systems/`)) ?? paths.find((path) => !path.slice(folder.length + 1).includes('/')) ?? paths[0] ?? null;
    }, [editor]);

    // Expose imperative handle for parent components
    useImperativeHandle(ref, () => ({
      getAllSystems: editor.getAllSystems,
      resetToDefaults: () => editor.loadSystems(defaultSystemsRef.current),
      setDefaults: (systems: SandboxSystems) => {
        defaultSystemsRef.current = systems;
      },
      loadSystems: (systems: SandboxSystems) => editor.loadSystems(systems),
      setOnSystemsChanged: (callback: SystemsChangedCallback) => {
        onSystemsChangedRef.current = callback;
      },
      switchToFolder: (folder: 'client' | 'server' | 'shared', subfolder?: string) => {
        const first = firstFileUnder(`src/${folder}/${subfolder || 'systems'}`);
        if (first) editor.open(first);
      },
      getModels: () => models.current,
      addNewFile: editor.addNewFile,
      addFolder: editor.addFolder,
      // The hook writes the tree synchronously, so back-to-back calls for one new path make one file.
      updateFile: editor.updateFile,
      markCodeCheckpoint: editor.bumpSyncEpoch,
      validateAllFiles: async () => {
        const results: { path: string; errors: string[] }[] = [];
        for (const path of Object.keys(editor.getProject().files)) {
          if (!isScriptPath(path) || !/^src\/(client|server|shared)\//.test(path)) continue;
          results.push({ path: path.slice('src/'.length), errors: await validateFile(models.current, path) });
        }
        return results;
      },
      getProject: editor.getProject,
      loadProject: editor.loadProject,
      getAiFiles: editor.getAiFiles,
    }), [editor, firstFileUnder]);

    /**
     * Opens a file the Tests tab names — a spec by its v1 path (`shared/__tests__/X.spec.ts`, `__tests__/x.spec.ts`),
     * a playground by its tree path (`sandbox.playground.ts`) — at `line` when given (1-based). Returns true if the
     * project has the file.
     */
    const openFileByPath = useCallback((path: string, line?: number): boolean => {
      const files = editor.tree.files;
      const asV1 = treePathOfV1Path(path);
      const bare = path.replace(/^\/+/, '');
      const treePath = asV1 !== null && Object.hasOwn(files, asV1) ? asV1 : Object.hasOwn(files, bare) ? bare : null;
      if (treePath === null) return false;
      editor.open(treePath);
      if (line && line > 0) {
        setRevealTarget({ line, nonce: ++revealNonce.current });
      }
      return true;
    }, [editor]);

    // Prevent keyboard events from propagating to game input handlers
    const stopKeyboardPropagation = useCallback((e: React.KeyboardEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <>
        <div 
          className="file-explorer"
          onKeyDown={stopKeyboardPropagation}
          onKeyUp={stopKeyboardPropagation}
        >
          <div className="sidebar-tabs">
            <button
              className={`sidebar-tab${sidebarTab === 'files' ? ' active' : ''}`}
              onClick={() => setSidebarTab('files')}
              data-testid="sidebar-tab-files"
            >
              Files
            </button>
            <button
              className={`sidebar-tab${sidebarTab === 'tests' ? ' active' : ''}`}
              onClick={() => setSidebarTab('tests')}
              data-testid="sidebar-tab-tests"
            >
              Tests
            </button>
            <button
              className={`sidebar-tab${sidebarTab === 'design' ? ' active' : ''}`}
              onClick={() => setSidebarTab('design')}
              data-testid="sidebar-tab-design"
            >
              Design
            </button>
            <button
              className={`sidebar-tab${sidebarTab === 'graph' ? ' active' : ''}`}
              onClick={() => setSidebarTab('graph')}
              data-testid="sidebar-tab-graph"
            >
              Graph
            </button>
            <button
              className={`sidebar-tab${sidebarTab === 'assets' ? ' active' : ''}`}
              onClick={() => setSidebarTab('assets')}
              data-testid="sidebar-tab-assets"
            >
              Assets
            </button>
          </div>
          {sidebarTab === 'files' && (
            <FileTree
              tree={tree}
              errors={errors}
              currentPath={currentPath}
              expandedFolders={expandedFolders}
              onToggleFolder={editor.toggleFolder}
              onFileClick={editor.open}
              onNewFile={editor.newFile}
              onNewFolder={editor.newFolder}
              onDeleteFile={editor.deleteFile}
              onRenameFile={editor.renameFile}
              onCheckRename={editor.checkRename}
              onRenameFolder={editor.renameFolder}
              onDeleteFolder={editor.deleteFolder}
              onCopyFile={editor.copyFile}
              onCutFile={editor.cutFile}
              onPasteFile={editor.pasteFile}
              hasClipboard={editor.hasClipboard}
              hasImportReferences={editor.hasImportReferences}
              canDelete={editor.canDelete}
              canRename={editor.canRename}
              canDeleteFolder={editor.canDeleteFolder}
              canRenameFolder={editor.canRenameFolder}
            />
          )}
          {sidebarTab === 'tests' && (
            <TestRunnerPanel
              project={tree}
              getProject={editor.getProject}
              onOpenFile={openFileByPath}
            />
          )}
          {sidebarTab === 'design' && (
            <DesignDocsPanel />
          )}
          {sidebarTab === 'graph' && (
            <KnowledgeGraphPanel />
          )}
          {sidebarTab === 'assets' && (
            <AssetsPanel />
          )}
        </div>
        <div
          ref={sidebarResizerRef}
          className="gutter sidebar-gutter"
          id="sidebar-resizer"
          title="Drag to resize sidebar"
        />
        <div
          id="editor-panel"
          className="editor-panel"
          onKeyDown={stopKeyboardPropagation}
          onKeyUp={stopKeyboardPropagation}
        >
          {sidebarTab !== 'tests' && (
            <div className="editor-tabs main-tabs">
              <button
                className={`editor-tab${currentPath?.startsWith('src/client/') ? ' active' : ''}`}
                onClick={() => {
                  const first = firstFileUnder('src/client');
                  if (first) editor.open(first);
                }}
              >
                Client
              </button>
              <button
                className={`editor-tab${currentPath?.startsWith('src/server/') ? ' active' : ''}`}
                onClick={() => {
                  const first = firstFileUnder('src/server');
                  if (first) editor.open(first);
                }}
              >
                Server
              </button>
            </div>
          )}
          <EditorTabs
            tree={tree}
            errors={errors}
            openTabs={openTabs}
            currentPath={currentPath}
            onTabClick={editor.open}
            onTabClose={(path) => editor.closeTab(path, openTabs)}
          />
          <MonacoEditor
            models={models}
            modelsReady={modelsReady}
            modelsVersion={editor.modelsVersion}
            currentPath={currentPath}
            isReadOnly={editor.isReadOnly}
            onErrorsChange={editor.setFileErrors}
            revealTarget={revealTarget}
          />
        </div>
      </>
    );
  }
);

// Re-export types for convenience
export type { SystemFile, SandboxSystems, SystemsChangedCallback } from './types';
